import { toJSON } from '@utils';

const tokenTypeSymbol = Symbol('TokenType');
export type TokenType = string & { readonly [tokenTypeSymbol]: unique symbol };

export interface MatchRange {
  text: string;
  start: number;
  end: number;
}

export class Token<T = unknown> {
  text: string;
  #start?: number;
  #end?: number;
  #value?: T;
  #type?: TokenType;

  constructor(
    text: string,
    opts?: { start?: number; end?: number; value?: T; type?: TokenType }
  ) {
    this.text = text;
    this.#start = opts?.start;
    this.#end = opts?.end;
    this.#value = opts?.value;
    this.#type = opts?.type;
  }

  clone(): Token<T> {
    return new Token(this.text, {
      start: this.#start,
      end: this.#end,
      value: this.#value,
      type: this.#type
    });
  }

  get start(): number {
    if (this.#start === undefined) {
      // throw new Error("Start not set");
      return undefined as unknown as number;
    }
    return this.#start;
  }

  set start(start: number) {
    this.#start = start;
  }

  get end(): number {
    if (this.#end === undefined) {
      // throw new Error("End not set");
      return undefined as unknown as number;
    }
    return this.#end;
  }

  set end(end: number) {
    this.#end = end;
  }

  get length(): number {
    return this.end - this.start;
  }

  get value(): T {
    if (this.#value === undefined) {
      throw new Error('Value not set');
    }
    return this.#value;
  }

  set value(value: T) {
    this.#value = value;
  }

  get type(): TokenType {
    if (this.#type === undefined) {
      throw new Error('Type not set');
    }
    return this.#type;
  }

  set type(type: TokenType) {
    this.#type = type;
  }

  toJSON(opts?: { compact?: boolean }): unknown {
    if (opts?.compact) {
      return this.toString(opts);
    }
    return {
      type: this.#type,
      value: this.#value,
      text: this.text,
      start: this.start,
      end: this.end
    };
  }

  toString(opts?: { compact?: boolean }): string {
    const components: unknown[] = [];
    components.push(`[${this.start}-${this.end}]`);
    if (this.#type !== undefined) {
      components.push(this.#type);
    }
    const value = this.#value;
    if (value !== undefined && value !== null) {
      components.push(':');
      components.push(toJSON(value, opts));
    }
    return components.join(' ');
  }
}

export type NumberToken = Token<number>;
export type StringToken = Token<string>;
